import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Sparkles, Loader2, Play, Pause, ChevronDown, ChevronUp,
  Zap, Target, TrendingUp, Star, AlertTriangle, Copy,
  Film, Music, Volume2, FileText, ArrowRight, RefreshCw,
  CheckCircle2, Clock, ExternalLink, Share2, Trophy, Layers,
  PenTool, Mic, BarChart3, Download, Flame, Crosshair, Package, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram Reels' },
  { value: 'instagram_story', label: 'Instagram Story' },
  { value: 'instagram_feed', label: 'Instagram Feed' },
  { value: 'youtube_shorts', label: 'YouTube Shorts' },
  { value: 'youtube_ad', label: 'YouTube Pre-roll' },
  { value: 'facebook', label: 'Facebook Feed' },
  { value: 'twitter', label: 'X (Twitter)' },
  { value: 'linkedin', label: 'LinkedIn' },
];
const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'emotional', label: 'Emotional' },
  { value: 'story', label: 'Story' },
  { value: 'authority', label: 'Authority' },
  { value: 'conversational', label: 'Conversational' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'authoritative', label: 'Authoritative' },
];
const DURATIONS = [
  { value: 15, label: '15s' },
  { value: 30, label: '30s' },
  { value: 45, label: '45s' },
  { value: 60, label: '60s' },
];

const CAMPAIGN_TEMPLATES = [
  {
    id: 'ugc_product',
    name: 'UGC Product Ad',
    description: 'Perfect for influencer-style product promotions.',
    icon: Film,
    color: 'from-orange-500/20 to-amber-500/20 border-orange-500/30',
    preset: { tone: 'conversational', duration: 30, num_creatives: 5 },
  },
  {
    id: 'saas_demo',
    name: 'SaaS Demo Ad',
    description: 'Authoritative demos for software products.',
    icon: Layers,
    color: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30',
    preset: { tone: 'authoritative', duration: 45, num_creatives: 5 },
  },
  {
    id: 'ecommerce',
    name: 'Ecommerce Conversion Ad',
    description: 'High-energy ads that drive purchase decisions.',
    icon: Zap,
    color: 'from-green-500/20 to-emerald-500/20 border-green-500/30',
    preset: { tone: 'energetic', duration: 30, num_creatives: 5 },
  },
  {
    id: 'lead_gen',
    name: 'Lead Generation Ad',
    description: 'Persuasive content to capture qualified leads.',
    icon: Target,
    color: 'from-purple-500/20 to-violet-500/20 border-purple-500/30',
    preset: { tone: 'persuasive', duration: 45, num_creatives: 5 },
  },
  {
    id: 'local_business',
    name: 'Local Business Ad',
    description: 'Warm, friendly ads for local service providers.',
    icon: Star,
    color: 'from-pink-500/20 to-rose-500/20 border-pink-500/30',
    preset: { tone: 'friendly', duration: 30, num_creatives: 5 },
  },
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-zinc-500', bg: 'bg-zinc-800', icon: Clock },
  queued: { label: 'Queued', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Clock },
  rendering: { label: 'Rendering', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Loader2 },
  ready: { label: 'Ready', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/10', icon: AlertTriangle },
};

// Granular creative generation step config
const GENERATION_STEPS = [
  { key: 'generating_script', label: 'Scripting', icon: PenTool, description: 'Writing hook & script' },
  { key: 'generating_voice', label: 'Voice', icon: Mic, description: 'Generating narration' },
  { key: 'generating_music', label: 'Music', icon: Music, description: 'Composing background' },
  { key: 'scoring', label: 'Scoring', icon: BarChart3, description: 'AI analysis' },
  { key: 'ready', label: 'Complete', icon: CheckCircle2, description: 'Ready' },
];

const getStepIndex = (status) => {
  const idx = GENERATION_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : status === 'failed' ? -1 : 0;
};

// Campaign Generation Progress Panel
const CampaignProgressPanel = ({ campaignId, numCreatives, onComplete, token, creditBalance }) => {
  const [progress, setProgress] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!campaignId) return;
    let active = true;

    const poll = async () => {
      try {
        const resp = await axios.get(`${API}/campaigns/${campaignId}/status`, { headers });
        if (!active) return;
        setProgress(resp.data);

        if (resp.data.status === 'complete') {
          // Fetch full campaign data
          const fullResp = await axios.get(`${API}/campaigns/${campaignId}`, { headers });
          onComplete(fullResp.data);
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        if (active) setTimeout(poll, 5000);
      }
    };
    poll();

    return () => { active = false; };
  }, [campaignId]);

  if (!progress) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
        <p className="text-zinc-400">Initializing campaign...</p>
      </div>
    );
  }

  const creatives = progress.creative_progress || [];
  const readyCount = creatives.filter(c => c.status === 'ready').length;
  const totalCount = numCreatives || creatives.length || 1;
  const overallPct = Math.round((readyCount / totalCount) * 100);

  // Calculate weighted progress including in-progress creatives
  let weightedProgress = 0;
  creatives.forEach(c => {
    const stepIdx = getStepIndex(c.status);
    if (c.status === 'ready') weightedProgress += 100;
    else if (c.status === 'failed') weightedProgress += 100;
    else if (stepIdx >= 0) weightedProgress += (stepIdx / (GENERATION_STEPS.length - 1)) * 100;
  });
  const smoothPct = Math.round(weightedProgress / totalCount);

  return (
    <div className="space-y-6" data-testid="campaign-progress-panel">
      {/* Overall Progress */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-zinc-100">Generating Campaign</h3>
          <div className="flex items-center gap-3">
            {creditBalance && (
              <span className="text-xs text-zinc-500" data-testid="progress-credit-balance">
                Credits: <span className="text-amber-400 font-bold">{(creditBalance.balance || 0).toLocaleString()}</span>
              </span>
            )}
            <span className="text-sm text-zinc-400">{readyCount}/{totalCount} complete</span>
          </div>
        </div>
        <div className="h-3 bg-zinc-800 rounded-full overflow-hidden mb-2">
          <div
            className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-1000 ease-out"
            style={{ width: `${Math.max(5, smoothPct)}%` }}
            data-testid="campaign-progress-bar"
          />
        </div>
        <p className="text-xs text-zinc-500 text-right">{smoothPct}%</p>
      </div>

      {/* Per-Creative Progress Cards */}
      <div className="grid gap-3 md:grid-cols-2">
        {creatives.map((c, idx) => {
          const stepIdx = getStepIndex(c.status);
          const isComplete = c.status === 'ready';
          const isFailed = c.status === 'failed';

          return (
            <div
              key={c.id || idx}
              className={`border rounded-xl p-4 transition-all duration-500 ${
                isComplete
                  ? 'border-green-500/30 bg-green-500/5 animate-fade-in-up'
                  : isFailed
                  ? 'border-red-500/30 bg-red-500/5'
                  : 'border-zinc-800 bg-zinc-900/60'
              }`}
              style={{ animationDelay: isComplete ? `${idx * 150}ms` : '0ms' }}
              data-testid={`creative-progress-${c.letter || idx}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isComplete ? 'bg-green-500/20' : isFailed ? 'bg-red-500/20' : 'bg-zinc-800'
                  }`}>
                    <span className={`text-sm font-bold ${
                      isComplete ? 'text-green-400' : isFailed ? 'text-red-400' : 'text-zinc-400'
                    }`}>{c.letter || chr65(idx)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-200">Creative {c.letter || chr65(idx)}</p>
                    <p className="text-[10px] text-zinc-500">{c.framework_label || ''}</p>
                  </div>
                </div>
                {isComplete && c.overall_score > 0 && (
                  <span className={`text-lg font-black ${
                    c.overall_score >= 80 ? 'text-green-400' : c.overall_score >= 60 ? 'text-amber-400' : 'text-red-400'
                  }`}>{c.overall_score}</span>
                )}
              </div>

              {/* Step Checklist */}
              <div className="flex items-center gap-1">
                {GENERATION_STEPS.map((step, si) => {
                  const StepIcon = step.icon;
                  const isPast = isComplete || (stepIdx > si);
                  const isCurrent = !isComplete && !isFailed && stepIdx === si;

                  return (
                    <div key={step.key} className="flex items-center gap-1 flex-1">
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full transition-all ${
                        isPast ? 'bg-green-500/20 text-green-400' :
                        isCurrent ? 'bg-orange-500/20 text-orange-400' :
                        'bg-zinc-800 text-zinc-600'
                      }`}>
                        {isPast ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : isCurrent ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <StepIcon className="w-3 h-3" />
                        )}
                      </div>
                      {si < GENERATION_STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 rounded ${isPast ? 'bg-green-500/40' : 'bg-zinc-800'}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-zinc-500 mt-2 capitalize">
                {isFailed ? 'Failed' : isComplete ? 'Complete' : GENERATION_STEPS[stepIdx]?.description || c.status}
              </p>
              {/* Intelligence signals during generation */}
              {!isComplete && !isFailed && c.framework_label && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`intel-signals-${c.letter || idx}`}>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    Framework: {c.framework_label}
                  </span>
                  {stepIdx >= 1 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20 animate-fade-in-up">
                      Hook strength: High
                    </span>
                  )}
                </div>
              )}
              {/* Intelligence signals for completed creatives */}
              {isComplete && c.overall_score > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`intel-complete-${c.letter || idx}`}>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    {c.framework_label}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    c.overall_score >= 75 ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                  }`}>
                    Score: {c.overall_score}
                  </span>
                </div>
              )}
              {/* Show hook preview when available */}
              {c.hook && (
                <p className="text-xs text-zinc-400 mt-2 line-clamp-1 italic">"{c.hook}"</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper for letter fallback
const chr65 = (i) => String.fromCharCode(65 + i);

// Compute outcome badges for a set of creatives
const computeBadges = (creatives) => {
  if (!creatives?.length) return {};
  const badges = {};
  let bestScore = -1, bestHook = -1, bestConversion = -1, bestEngagement = -1;
  let winnerId = null, hookId = null, conversionId = null, engagementId = null;

  creatives.forEach(c => {
    const score = c.overall_score || 0;
    const hook = c.scores?.hook_strength || 0;
    const conv = c.scores?.conversion_probability || 0;
    const engagement = (c.preview_views || 0) + (c.preview_clicks || 0);

    if (score > bestScore) { bestScore = score; winnerId = c.id; }
    if (hook > bestHook) { bestHook = hook; hookId = c.id; }
    if (conv > bestConversion) { bestConversion = conv; conversionId = c.id; }
    if (engagement > bestEngagement) { bestEngagement = engagement; engagementId = c.id; }
  });

  if (winnerId) badges[winnerId] = [...(badges[winnerId] || []), { label: 'WINNER', icon: Star, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' }];
  if (hookId) badges[hookId] = [...(badges[hookId] || []), { label: 'Best Hook', icon: Flame, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' }];
  if (conversionId) badges[conversionId] = [...(badges[conversionId] || []), { label: 'High Conversion', icon: Crosshair, color: 'text-green-400 bg-green-500/10 border-green-500/20' }];
  if (engagementId && bestEngagement > 0) badges[engagementId] = [...(badges[engagementId] || []), { label: 'Top Engagement', icon: TrendingUp, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' }];

  return badges;
};

// Badge pill component
const BadgePill = ({ badge }) => {
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${badge.color}`} data-testid={`badge-${badge.label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className="w-3 h-3" />
      {badge.label}
    </span>
  );
};

const VideoStatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.bg} ${cfg.color}`} data-testid={`video-status-${status}`}>
      <Icon className={`w-2.5 h-2.5 ${status === 'rendering' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
};

const ScoreBar = ({ label, value, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className="text-zinc-300 font-medium">{value}</span>
    </div>
    <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, background: color }} />
    </div>
  </div>
);

const CreativeCard = ({ creative, isRecommended, onRenderVideo, onSharePreview, renderingId, badges, isDemoMode }) => {
  const [expanded, setExpanded] = useState(false);
  const scores = creative.scores || {};
  const overall = creative.overall_score || 0;
  const scoreColor = overall >= 80 ? '#22c55e' : overall >= 60 ? '#f59e0b' : '#ef4444';
  const videoStatus = creative.video_status || 'pending';
  const creativeBadges = badges || [];

  return (
    <div className={`relative border rounded-2xl p-5 transition-all ${
      isRecommended ? 'border-orange-500/50 bg-orange-500/5' : 'border-zinc-800 bg-zinc-900/60'
    }`} data-testid={`creative-card-${creative.letter}`}>
      {isRecommended && (
        <div className="absolute -top-3 left-4 flex items-center gap-1 px-3 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
          <Trophy className="w-3 h-3" /> RECOMMENDED
        </div>
      )}

      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-zinc-100">Creative {creative.letter}</h3>
            <VideoStatusBadge status={renderingId === creative.id ? 'rendering' : videoStatus} />
          </div>
          <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">{creative.framework_label}</span>
          {/* Outcome Badges */}
          {creativeBadges.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`badges-${creative.letter}`}>
              {creativeBadges.map((b, i) => <BadgePill key={i} badge={b} />)}
            </div>
          )}
        </div>
        <div className="text-center">
          <div className="text-2xl font-black" style={{ color: scoreColor }}>{overall}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Score</div>
        </div>
      </div>

      {/* Hook */}
      <div className="mb-3 p-3 bg-zinc-800/50 rounded-xl border-l-2 border-orange-500">
        <p className="text-sm text-zinc-500 mb-0.5 font-medium">Hook</p>
        <p className="text-zinc-200 font-medium text-sm leading-relaxed">{creative.hook}</p>
      </div>

      {/* Score Bars */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
        <ScoreBar label="Hook Strength" value={scores.hook_strength || 0} color="#f97316" />
        <ScoreBar label="Emotional Trigger" value={scores.emotional_trigger || 0} color="#ec4899" />
        <ScoreBar label="Clarity" value={scores.clarity || 0} color="#3b82f6" />
        <ScoreBar label="Platform Opt." value={scores.platform_optimization || 0} color="#8b5cf6" />
        <ScoreBar label="Conversion Prob." value={scores.conversion_probability || 0} color="#22c55e" />
      </div>

      {/* Improvements */}
      {creative.improvements?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-zinc-500 font-medium mb-1">Improvement Suggestions</p>
          <div className="space-y-1">
            {creative.improvements.map((imp, j) => (
              <div key={j} className="flex items-start gap-1.5 text-xs text-amber-400/80">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{imp}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expand/Collapse */}
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mb-3">
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Hide Details' : 'Show Script & Assets'}
      </button>

      {expanded && (
        <div className="space-y-3 mb-3 animate-in fade-in duration-200">
          <div className="p-3 bg-zinc-800/30 rounded-xl">
            <p className="text-xs text-zinc-500 font-medium mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Script</p>
            <p className="text-sm text-zinc-300 whitespace-pre-line leading-relaxed">{creative.script}</p>
          </div>
          <div className="p-3 bg-zinc-800/30 rounded-xl">
            <p className="text-xs text-zinc-500 font-medium mb-1">Caption</p>
            <p className="text-sm text-zinc-300">{creative.caption}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {creative.voice_url && (
              <div className="flex-1 min-w-[200px] p-2 bg-zinc-800/30 rounded-xl">
                <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1"><Volume2 className="w-3 h-3" /> Voice</p>
                <audio controls src={`${BACKEND_URL}${creative.voice_url}`} className="w-full h-8" preload="none" />
              </div>
            )}
            {creative.music_url && (
              <div className="flex-1 min-w-[200px] p-2 bg-zinc-800/30 rounded-xl">
                <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1"><Music className="w-3 h-3" /> Music</p>
                <audio controls src={`${BACKEND_URL}${creative.music_url}`} className="w-full h-8" preload="none" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions — hidden in demo mode */}
      {!isDemoMode && (
      <div className="flex gap-2 flex-wrap">
        {videoStatus === 'ready' && creative.video_url ? (
          <Button size="sm" variant="outline" className="text-green-400 border-green-500/30" disabled>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Video Ready
          </Button>
        ) : videoStatus === 'rendering' || videoStatus === 'queued' || renderingId === creative.id ? (
          <Button size="sm" variant="outline" disabled>
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {videoStatus === 'queued' ? 'Queued...' : 'Rendering...'}
          </Button>
        ) : videoStatus === 'failed' ? (
          <Button size="sm" variant="outline" className="text-red-400 border-red-500/30"
            onClick={() => onRenderVideo(creative.id)} data-testid={`retry-render-${creative.letter}`}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry Render
          </Button>
        ) : (
          <Button size="sm" className={isRecommended ? 'bg-orange-500 hover:bg-orange-600' : ''}
            onClick={() => onRenderVideo(creative.id)} data-testid={`render-video-${creative.letter}`}>
            <Film className="w-3.5 h-3.5 mr-1" /> Render Video
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => onSharePreview(creative.id)} data-testid={`share-preview-${creative.letter}`}>
          <Share2 className="w-3.5 h-3.5 mr-1" /> Share Preview
        </Button>
      </div>
      )}
    </div>
  );
};

export default function CampaignPage() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('generate');
  const [form, setForm] = useState({ product: '', audience: '', platform: 'tiktok', tone: 'professional', num_creatives: 5, duration: 30 });
  const [generating, setGenerating] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [renderingId, setRenderingId] = useState(null);
  const [batchRendering, setBatchRendering] = useState(false);
  const [generatingCampaignId, setGeneratingCampaignId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [productUrl, setProductUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractedProduct, setExtractedProduct] = useState(null);
  
  const isDemoMode = user?.demo_mode && user?.is_founder;

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch credit balance
  useEffect(() => {
    if (!token) return;
    const fetchCredits = async () => {
      try {
        const resp = await axios.get(`${API}/credits/balance`, { headers });
        setCreditBalance(resp.data);
      } catch {}
    };
    fetchCredits();
  }, [token, generating]);

  // Campaign cost estimate (per creative)
  const COST_PER_CREATIVE = { scripts: 2, voice: 3, music: 2 };
  const VIDEO_COST_PER_CREATIVE = 12;
  const costEstimate = useMemo(() => {
    const n = form.num_creatives || 5;
    const scripts = COST_PER_CREATIVE.scripts * n;
    const voice = COST_PER_CREATIVE.voice * n;
    const music = COST_PER_CREATIVE.music * n;
    const video = VIDEO_COST_PER_CREATIVE * n;
    return { scripts, voice, music, video, total: scripts + voice + music + video, generation: scripts + voice + music };
  }, [form.num_creatives]);

  const applyTemplate = (template) => {
    setSelectedTemplate(template.id);
    setForm(prev => ({ ...prev, ...template.preset }));
  };

  // Pre-fill from URL params (Use in Campaign flow)
  useEffect(() => {
    const product = searchParams.get('product');
    const platform = searchParams.get('platform');
    const tone = searchParams.get('tone');
    if (product || platform || tone) {
      setForm(prev => ({
        ...prev,
        ...(product && { product }),
        ...(platform && { platform }),
        ...(tone && { tone }),
      }));
    }
  }, [searchParams]);

  const fetchCampaigns = useCallback(async () => {
    try {
      if (isDemoMode) {
        const resp = await axios.get(`${API}/demo/campaigns`, { headers });
        setCampaigns(resp.data);
      } else {
        const resp = await axios.get(`${API}/campaigns`, { headers });
        setCampaigns(resp.data);
      }
    } catch {}
  }, [token, isDemoMode]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // Poll video statuses when batch rendering
  useEffect(() => {
    if (!batchRendering || !activeCampaign) return;
    const interval = setInterval(async () => {
      try {
        const resp = await axios.get(`${API}/campaigns/${activeCampaign.id}`, { headers });
        setActiveCampaign(resp.data);
        const creatives = resp.data.creatives || [];
        const stillRendering = creatives.some(c => c.video_status === 'rendering' || c.video_status === 'queued');
        if (!stillRendering) {
          setBatchRendering(false);
          const readyCount = creatives.filter(c => c.video_status === 'ready').length;
          toast.success(`Batch render complete: ${readyCount} videos ready`);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [batchRendering, activeCampaign?.id]);

  const handleGenerate = async () => {
    if (!form.product || !form.audience) { toast.error('Product and Audience are required'); return; }
    setGenerating(true);
    try {
      const resp = await axios.post(`${API}/campaigns/generate`, form, { headers });
      const campId = resp.data.id;
      toast.success('Campaign generating...');
      setGeneratingCampaignId(campId);
      setTab('dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
      setGenerating(false);
    }
  };

  const handleGenerationComplete = (campaignData) => {
    setActiveCampaign(campaignData);
    setGenerating(false);
    setGeneratingCampaignId(null);
    fetchCampaigns();
    toast.success('Campaign ready!');
  };

  const handleRenderVideo = async (creativeId) => {
    setRenderingId(creativeId);
    try {
      await axios.post(`${API}/campaigns/creatives/${creativeId}/render-video`, {}, { headers });
      toast.success('Video rendering started');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Render failed');
      setRenderingId(null);
    }
  };

  const handleBatchRenderTop3 = async () => {
    if (!activeCampaign) return;
    setBatchRendering(true);
    try {
      const resp = await axios.post(`${API}/campaigns/${activeCampaign.id}/render-top`, {}, { headers });
      const data = resp.data;
      if (data.status === 'nothing_to_render') {
        toast.info('All top creatives already rendered');
        setBatchRendering(false);
      } else {
        toast.success(`Rendering ${data.count} videos (Top 3 by score)`);
        // Refresh campaign to show queued status
        const fullResp = await axios.get(`${API}/campaigns/${activeCampaign.id}`, { headers });
        setActiveCampaign(fullResp.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch render failed');
      setBatchRendering(false);
    }
  };

  const handleSharePreview = (creativeId) => {
    const url = `${window.location.origin}/preview/${creativeId}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Preview link copied!')).catch(() => toast.error('Copy failed'));
  };

  const handleExtractProduct = async () => {
    if (!productUrl.trim()) return toast.error('Paste a product URL');
    setExtracting(true);
    try {
      const resp = await axios.post(`${API}/campaigns/extract-product`, { url: productUrl.trim() }, { headers });
      const data = resp.data;
      setExtractedProduct(data);
      setForm(prev => ({
        ...prev,
        product: data.product || data.title || prev.product,
        audience: data.audience || prev.audience,
      }));
      toast.success('Product info extracted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not extract product info');
    } finally {
      setExtracting(false);
    }
  };

  const loadCampaign = async (campId) => {
    try {
      if (isDemoMode) {
        const resp = await axios.get(`${API}/demo/campaigns/${campId}`, { headers });
        setActiveCampaign(resp.data);
      } else {
        const resp = await axios.get(`${API}/campaigns/${campId}`, { headers });
        setActiveCampaign(resp.data);
      }
      setTab('dashboard');
    } catch { toast.error('Failed to load campaign'); }
  };

  const hasRenderableCreatives = activeCampaign?.creatives?.some(
    c => c.status === 'ready' && (!c.video_status || c.video_status === 'pending' || c.video_status === 'failed')
  );

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Demo Mode Banner */}
        {isDemoMode && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center gap-3" data-testid="demo-mode-banner">
            <Eye className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400 flex-1">Demo Mode — Showing curated campaigns. Editing and generation are locked.</p>
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-zinc-100">Campaign Generator</h1>
            <p className="text-sm text-zinc-500 mt-1">Generate, score, and share ad creatives</p>
          </div>
          <div className="flex gap-2">
            <Button variant={tab === 'generate' ? 'default' : 'outline'} size="sm" onClick={() => setTab('generate')} data-testid="tab-generate">
              <Sparkles className="w-4 h-4 mr-1" /> Generate
            </Button>
            <Button variant={tab === 'dashboard' ? 'default' : 'outline'} size="sm" onClick={() => setTab('dashboard')} data-testid="tab-dashboard">
              <TrendingUp className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <Button variant={tab === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setTab('history')} data-testid="tab-history">
              <Clock className="w-4 h-4 mr-1" /> History
            </Button>
          </div>
        </div>

        {/* GENERATE TAB */}
        {tab === 'generate' && (
          <div className="max-w-3xl">
            {/* Quick Start from Product URL */}
            <div className="mb-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-2xl p-5" data-testid="product-url-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <ExternalLink className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-zinc-100">Quick Start from URL</h2>
                  <p className="text-[11px] text-zinc-500">Paste a product link and we'll auto-generate your campaign brief</p>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 bg-zinc-900/80 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-orange-500 placeholder:text-zinc-600"
                  placeholder="https://your-product.com/..."
                  value={productUrl}
                  onChange={e => setProductUrl(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleExtractProduct()}
                  data-testid="input-product-url"
                />
                <Button onClick={handleExtractProduct} disabled={extracting || !productUrl.trim()}
                  className="bg-orange-500 hover:bg-orange-400 gap-1.5 px-5" data-testid="extract-product-btn">
                  {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {extracting ? 'Extracting...' : 'Extract'}
                </Button>
              </div>
              {extractedProduct && (
                <div className="mt-3 flex items-start gap-3 bg-zinc-900/50 rounded-lg p-3 border border-zinc-800">
                  {extractedProduct.image_url && (
                    <img src={extractedProduct.image_url} alt="" className="w-16 h-16 rounded-lg object-cover border border-zinc-700 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-zinc-300 truncate">{extractedProduct.title}</p>
                    {extractedProduct.price && <p className="text-[11px] text-emerald-400 font-semibold mt-0.5">{extractedProduct.price}</p>}
                    <p className="text-[10px] text-zinc-500 mt-1 line-clamp-2">{extractedProduct.description}</p>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                </div>
              )}
            </div>

            {/* Template Selection Panel */}
            <div className="mb-6" data-testid="template-selection-panel">
              <h2 className="text-lg font-semibold text-zinc-100 mb-1">Start with a Template</h2>
              <p className="text-xs text-zinc-500 mb-4">Choose a template to pre-fill your campaign settings, or start from scratch below.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {CAMPAIGN_TEMPLATES.map((tpl) => {
                  const Icon = tpl.icon;
                  const isActive = selectedTemplate === tpl.id;
                  return (
                    <button
                      key={tpl.id}
                      onClick={() => applyTemplate(tpl)}
                      className={`text-left p-4 rounded-xl border transition-all bg-gradient-to-br ${tpl.color} ${
                        isActive
                          ? 'ring-2 ring-orange-500/50 border-orange-500/60 scale-[1.02]'
                          : 'border-zinc-800 hover:border-zinc-700 hover:scale-[1.01]'
                      }`}
                      data-testid={`template-${tpl.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          isActive ? 'bg-orange-500/30' : 'bg-zinc-800/60'
                        }`}>
                          <Icon className={`w-4.5 h-4.5 ${isActive ? 'text-orange-400' : 'text-zinc-400'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-semibold ${isActive ? 'text-orange-300' : 'text-zinc-200'}`}>{tpl.name}</p>
                          <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{tpl.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6" data-testid="campaign-form">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-zinc-100">New Campaign</h2>
                {selectedTemplate && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20">
                    {CAMPAIGN_TEMPLATES.find(t => t.id === selectedTemplate)?.name}
                  </span>
                )}
              </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Product / Offer *</label>
                <input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-orange-500"
                  placeholder="e.g. ZapBlast Energy Drink — 2x caffeine, zero sugar"
                  value={form.product} onChange={e => setForm({...form, product: e.target.value})} data-testid="input-product" />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Target Audience *</label>
                <input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-orange-500"
                  placeholder="e.g. Gen-Z fitness enthusiasts aged 18-28"
                  value={form.audience} onChange={e => setForm({...form, audience: e.target.value})} data-testid="input-audience" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Platform</label>
                  <select className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200"
                    value={form.platform} onChange={e => setForm({...form, platform: e.target.value})} data-testid="select-platform">
                    {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Tone</label>
                  <select className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200"
                    value={form.tone} onChange={e => setForm({...form, tone: e.target.value})} data-testid="select-tone">
                    {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Duration</label>
                  <select className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200"
                    value={form.duration} onChange={e => setForm({...form, duration: parseInt(e.target.value)})} data-testid="select-duration">
                    {DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Number of Creatives: {form.num_creatives}</label>
                <input type="range" min={3} max={10} value={form.num_creatives}
                  onChange={e => setForm({...form, num_creatives: parseInt(e.target.value)})}
                  className="w-full accent-orange-500" data-testid="slider-num-creatives" />
                <div className="flex justify-between text-xs text-zinc-600"><span>3</span><span>10</span></div>
              </div>
              {/* Cost Estimate */}
              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-4 space-y-2" data-testid="cost-estimate-panel">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Estimated Credits</span>
                  {creditBalance && (
                    <span className="text-xs text-zinc-500">
                      Balance: <span className="text-amber-400 font-bold" data-testid="campaign-credit-balance">{(creditBalance.balance || 0).toLocaleString()}</span>
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <span className="text-zinc-500">Scripts</span>
                  <span className="text-zinc-300 text-right">{costEstimate.scripts}</span>
                  <span className="text-zinc-500">Voice</span>
                  <span className="text-zinc-300 text-right">{costEstimate.voice}</span>
                  <span className="text-zinc-500">Music</span>
                  <span className="text-zinc-300 text-right">{costEstimate.music}</span>
                  <span className="text-zinc-500">Video rendering</span>
                  <span className="text-zinc-300 text-right">{costEstimate.video}</span>
                </div>
                <div className="border-t border-zinc-700/50 pt-2 flex justify-between items-center">
                  <span className="text-xs font-semibold text-zinc-300">Estimated total</span>
                  <span className="text-sm font-bold text-amber-400" data-testid="cost-estimate-total">~{costEstimate.total} credits</span>
                </div>
                <p className="text-[10px] text-zinc-600">Generation: ~{costEstimate.generation} · Video rendering is optional and separate.</p>
              </div>

              <Button onClick={handleGenerate} disabled={generating || isDemoMode} className="w-full btn-oracle" size="lg" data-testid="generate-campaign-btn">
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Campaign...</> : <><Zap className="w-4 h-4 mr-2" /> Generate Campaign</>}
              </Button>
            </div>
          </div>
          </div>
        )}

        {/* DASHBOARD TAB */}
        {tab === 'dashboard' && (
          <div data-testid="campaign-dashboard">
            {generating && generatingCampaignId && (
              <CampaignProgressPanel
                campaignId={generatingCampaignId}
                numCreatives={form.num_creatives}
                onComplete={handleGenerationComplete}
                token={token}
                creditBalance={creditBalance}
              />
            )}
            {activeCampaign && !generating && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-zinc-100">{activeCampaign.product}</h2>
                    <p className="text-sm text-zinc-500">{activeCampaign.audience} · {activeCampaign.platform} · {activeCampaign.num_creatives} creatives</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Export & Render — hidden in demo mode */}
                    {!isDemoMode && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              toast.loading('Preparing export...', { id: 'export' });
                              const resp = await axios.get(`${API}/campaigns/${activeCampaign.id}/export`, {
                                headers, responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(resp.data);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `delphi_campaign_${activeCampaign.product?.replace(/\s/g, '_')}.zip`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                              toast.success('Campaign exported!', { id: 'export' });
                            } catch (e) {
                              toast.error('Export failed', { id: 'export' });
                            }
                          }}
                          data-testid="export-campaign-btn"
                        >
                          <Package className="w-3.5 h-3.5 mr-1" /> Export Package
                        </Button>
                        {hasRenderableCreatives && (
                          <Button
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600"
                            onClick={handleBatchRenderTop3}
                            disabled={batchRendering}
                            data-testid="render-top-3-btn"
                          >
                            {batchRendering ? (
                              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Rendering Top 3...</>
                            ) : (
                              <><Layers className="w-3.5 h-3.5 mr-1" /> Render Top 3</>
                            )}
                          </Button>
                        )}
                      </>
                    )}
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${activeCampaign.status === 'complete' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {activeCampaign.status}
                    </span>
                  </div>
                </div>
                {(() => {
                  const badgeMap = computeBadges(activeCampaign.creatives || []);
                  return (
                    <div className="grid gap-4 md:grid-cols-2">
                      {(activeCampaign.creatives || []).map(c => (
                        <CreativeCard
                          key={c.id}
                          creative={c}
                          isRecommended={activeCampaign.recommended_creative_id === c.id}
                          onRenderVideo={handleRenderVideo}
                          onSharePreview={handleSharePreview}
                          renderingId={renderingId}
                          badges={badgeMap[c.id]}
                          isDemoMode={isDemoMode}
                        />
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
            {!generating && !activeCampaign && (
              <div className="text-center py-16 text-zinc-500">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No campaign selected. Generate one or pick from history.</p>
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === 'history' && (
          <div className="space-y-3" data-testid="campaign-history">
            {campaigns.length === 0 && <p className="text-center py-12 text-zinc-500">No campaigns yet.</p>}
            {campaigns.map(c => (
              <button key={c.id} onClick={() => loadCampaign(c.id)}
                className="w-full text-left p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-200">{c.product}</p>
                    <p className="text-xs text-zinc-500">{c.audience} · {c.platform} · {c.num_creatives} creatives</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${c.status === 'complete' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {c.status}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-600" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
